import React, { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Search, ShoppingCart, Send, Loader2, PackageCheck } from "lucide-react";

export default function OrderList() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [cat, setCat] = useState("all");
  const [qtys, setQtys] = useState({});
  const [basketVendor, setBasketVendor] = useState({});

  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const { data, isLoading } = useQuery({
    queryKey: ["order-list", cat, qDebounced],
    queryFn: async () =>
      (await api.get(`/procurement/order-list?category=${encodeURIComponent(cat)}&q=${encodeURIComponent(qDebounced)}`)).data,
    placeholderData: keepPreviousData,
  });

  const rows = data?.rows || [];

  const generate = useMutation({
    mutationFn: async (lines) =>
      (await api.post("/procurement/bidding/generate-orders", { lines })).data,
    onSuccess: (d) => {
      setQtys({});
      setBasketVendor({});
      qc.invalidateQueries({ queryKey: ["orders"] });
      toast.success(d.orders === 1 ? "Στάλθηκε 1 παραγγελία" : `Δημιουργήθηκαν ${d.orders} παραγγελίες`);
      if (d.skipped?.length) toast.warning(`Παραλείφθηκαν ${d.skipped.length} είδη`);
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  const orderLines = useMemo(
    () => Object.entries(qtys).filter(([, v]) => Number(v) > 0).map(([item_id, v]) => ({ item_id, qty: Number(v) })),
    [qtys]
  );
  const vendorCount = useMemo(() => {
    const ids = new Set();
    orderLines.forEach((l) => { if (basketVendor[l.item_id]) ids.add(basketVendor[l.item_id]); });
    return ids.size;
  }, [orderLines, basketVendor]);

  const tabCls = (active) =>
    `shrink-0 rounded-full px-3 py-1 text-[11px] font-semibold whitespace-nowrap transition-colors ${
      active ? "bg-[#45423C] text-white" : "bg-[#F1EDE6] text-[#6F6A60] hover:bg-[#E7E1D7]"}`;

  return (
    <div className="space-y-6" data-testid="order-list-page">
      <div>
        <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">Προμήθειες</p>
        <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1">Λίστα Παραγγελίας</h1>
        <p className="text-[#6F6A60] text-sm mt-1">
          Βάλε ποσότητες στα είδη που χρειάζεσαι. Η εφαρμογή ετοιμάζει αυτόματα τις παραγγελίες, ομαδοποιημένες ανά προμηθευτή.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="h-4 w-4 text-[#6F6A60] absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="ol-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Αναζήτηση είδους"
            className="rounded-xl border border-[#E3DED4] bg-white pl-9 pr-3 py-2 text-sm outline-none focus:border-[#45423C]" />
        </div>
        <span className="text-sm text-[#6F6A60] ml-auto">{data?.count || 0} {(data?.count || 0) === 1 ? "είδος" : "είδη"}</span>
      </div>

      <div className="flex flex-wrap items-center gap-1.5" data-testid="ol-cat-tabs">
        <button data-testid="ol-cat-all" onClick={() => setCat("all")} className={tabCls(cat === "all")}>Όλες</button>
        {(data?.by_category || []).map((c) => (
          <button key={c.category} data-testid={`ol-cat-${c.category}`} onClick={() => setCat(c.category)} className={tabCls(cat === c.category)}>
            {c.category} ({c.count})
          </button>
        ))}
      </div>

      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden">
        <div className="px-5 py-4 border-b border-[#E3DED4] flex items-center gap-2">
          <ShoppingCart className="h-4 w-4 text-[#C86235]" />
          <h2 className="font-medium text-[#45423C]">Είδη προς παραγγελία</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#45423C] text-left text-[10px] uppercase tracking-wider text-white/80">
                <th className="px-4 py-3">Είδος</th>
                <th className="px-3 py-3">Κατηγορία</th>
                <th className="px-3 py-3">Προμηθευτής</th>
                <th className="px-3 py-3 text-center">Μονάδα</th>
                <th className="px-3 py-3 text-right">Ποσότητα</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={5} className="px-4 py-8 text-center text-[#6F6A60]">Φόρτωση…</td></tr>}
              {rows.map((r) => (
                <tr key={r.item_id} data-testid={`ol-row-${r.item_id}`} className="border-t border-[#E3DED4] hover:bg-[#F7F5F1]">
                  <td className="px-4 py-2.5 font-medium text-[#45423C]">{r.name}</td>
                  <td className="px-3 py-2.5 text-[#6F6A60] text-xs">{r.category}</td>
                  <td className="px-3 py-2.5 text-[#6F6A60] text-xs">{r.vendor_name}</td>
                  <td className="px-3 py-2.5 text-center text-xs">{r.unit}</td>
                  <td className="px-3 py-2.5 text-right">
                    <input data-testid={`ol-qty-${r.item_id}`} type="number" step="0.01" min="0"
                      value={qtys[r.item_id] ?? ""} placeholder="—"
                      onChange={(e) => { setQtys({ ...qtys, [r.item_id]: e.target.value }); setBasketVendor((m) => ({ ...m, [r.item_id]: r.vendor_id })); }}
                      className="w-24 rounded-lg border border-[#E3DED4] px-2 py-1.5 text-right text-sm tabular outline-none focus:border-[#45423C]" />
                  </td>
                </tr>
              ))}
              {!isLoading && rows.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-[#6F6A60]" data-testid="ol-empty">
                  Δεν υπάρχουν είδη με ορισμένο προμηθευτή ακόμη. Ο υπεύθυνος τα ορίζει στον Διαγωνισμό.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {orderLines.length > 0 && (
        <div className="sticky bottom-4 z-20 rounded-2xl bg-[#45423C] text-white shadow-xl px-5 py-4 flex flex-wrap items-center gap-4" data-testid="ol-basket">
          <PackageCheck className="h-5 w-5 text-[#E3B23C]" />
          <div className="text-sm">
            <strong className="tabular">{orderLines.length}</strong> {orderLines.length === 1 ? "είδος" : "είδη"} · {vendorCount} {vendorCount === 1 ? "παραγγελία" : "παραγγελίες"}
          </div>
          <button data-testid="ol-generate" onClick={() => generate.mutate(orderLines)} disabled={generate.isPending}
            className="ml-auto inline-flex items-center gap-2 rounded-xl bg-[#C86235] px-5 py-2.5 text-sm font-semibold hover:opacity-90 disabled:opacity-50">
            {generate.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Δημιουργία παραγγελιών
          </button>
        </div>
      )}
    </div>
  );
}
